import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/CompanyFormDialog";
import { RateBreakdown } from "@/components/common/RateBreakdown";
import { api, fmtDate, formatError } from "@/lib/api";
import { DEACTIVATION_REASONS, RATE_TYPES } from "@/lib/constants";

const todayIso = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Sydney" }).format(new Date());

export function DeactivateDialog({ contract, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(todayIso());
  const submit = async () => {
    try { const { data } = await api.post(`/contracts/${contract.id}/deactivate`, { reason, date }); toast.success("Contract deactivated"); onDone(data); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="deactivate-dialog">
        <DialogHeader><DialogTitle>Deactivate {contract.employment_id}</DialogTitle><DialogDescription>Sets the status to Inactive. A reason and date are required.</DialogDescription></DialogHeader>
        <Field label="Reason *">
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="deactivate-reason-select"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
            <SelectContent>{Object.entries(DEACTIVATION_REASONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Deactivated on *"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="deactivate-date-input" /></Field>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="destructive" disabled={!reason || !date} onClick={submit} data-testid="deactivate-confirm-button">Deactivate</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ExtendDialog({ contract, onClose, onDone }) {
  const [endDate, setEndDate] = useState("");
  const [withRate, setWithRate] = useState(false);
  const [rate, setRate] = useState({ gross_rate: "", rate_type: "hourly", effective_from: "", payme_fee_percent: "" });
  const submit = async () => {
    try {
      const { data } = await api.post(`/contracts/${contract.id}/extend`, { end_date: endDate, rate: withRate ? rate : null });
      toast.success(`Extended to ${fmtDate(endDate)}`);
      onDone(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="extend-dialog">
        <DialogHeader><DialogTitle>Extend {contract.employment_id}</DialogTitle><DialogDescription>Current end date: {fmtDate(contract.end_date)}</DialogDescription></DialogHeader>
        <Field label="New end date *"><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="extend-end-date-input" /></Field>
        <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={withRate} onChange={(e) => setWithRate(e.target.checked)} data-testid="extend-new-rate-checkbox" /> Add a new rate</label>
        {withRate && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Gross rate (AUD) *"><Input inputMode="decimal" className="font-mono" value={rate.gross_rate} onChange={(e) => setRate({ ...rate, gross_rate: e.target.value })} data-testid="extend-rate-input" /></Field>
            <Field label="Rate type">
              <Select value={rate.rate_type} onValueChange={(v) => setRate({ ...rate, rate_type: v })}>
                <SelectTrigger data-testid="extend-rate-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(RATE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Effective from *"><Input type="date" value={rate.effective_from} onChange={(e) => setRate({ ...rate, effective_from: e.target.value })} data-testid="extend-rate-effective-input" /></Field>
            <Field label="PayMe fee %"><Input inputMode="decimal" className="font-mono" value={rate.payme_fee_percent} onChange={(e) => setRate({ ...rate, payme_fee_percent: e.target.value })} data-testid="extend-rate-fee-input" /></Field>
            <div className="col-span-2"><RateBreakdown gross={rate.gross_rate} fee={rate.payme_fee_percent} effectiveFrom={rate.effective_from} testId="extend-rate-breakdown" /></div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button disabled={!endDate} onClick={submit} data-testid="extend-confirm-button">Extend contract</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddRateDialog({ contract, onClose, onDone }) {
  const [rate, setRate] = useState({ gross_rate: "", rate_type: "hourly", effective_from: todayIso(), payme_fee_percent: "", oncall_rate: "", onshore_rate: "", offshore_rate: "", notes: "" });
  const set = (k) => (e) => setRate({ ...rate, [k]: e.target ? e.target.value : e });
  const submit = async () => {
    try { await api.post(`/contracts/${contract.id}/rates`, rate); toast.success("Rate change added"); onDone(); } catch (e) { toast.error(formatError(e)); }
  };
  const num = (k, label) => <Field label={label}><Input inputMode="decimal" className="font-mono" value={rate[k]} onChange={set(k)} data-testid={`rate-${k}-input`} /></Field>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-rate-dialog">
        <DialogHeader><DialogTitle>Add rate change</DialogTitle><DialogDescription>Fee, super and taxable rate are calculated automatically using the super guarantee % in force on the effective date.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="grid grid-cols-2 gap-3 content-start">
            <Field label="Gross rate (AUD) *"><Input inputMode="decimal" className="font-mono" value={rate.gross_rate} onChange={set("gross_rate")} data-testid="rate-gross-input" /></Field>
            <Field label="Rate type">
              <Select value={rate.rate_type} onValueChange={set("rate_type")}>
                <SelectTrigger data-testid="rate-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(RATE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="PayMe fee %"><Input inputMode="decimal" className="font-mono" value={rate.payme_fee_percent} onChange={set("payme_fee_percent")} data-testid="rate-fee-input" /></Field>
            <Field label="Effective from *"><Input type="date" value={rate.effective_from} onChange={set("effective_from")} data-testid="rate-effective-input" /></Field>
            {num("oncall_rate", "On-call rate")}{num("onshore_rate", "Onshore rate")}{num("offshore_rate", "Offshore rate")}
          </div>
          <RateBreakdown gross={rate.gross_rate} fee={rate.payme_fee_percent} effectiveFrom={rate.effective_from} />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={!rate.gross_rate || !rate.effective_from} data-testid="rate-save-button">Add rate change</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
